// Gets a photo ready to send. HEIC becomes JPEG so everyone can open it. Photos larger than
// 2560 px on the long edge are scaled down in halves, which keeps them sharp, then encoded at the
// best quality that fits a size budget. EXIF orientation is applied to the pixels. What a photo
// says about where and when it was taken is removed. "Send original" skips the scaling and keeps
// the pixels as they are, but still removes that.

import type { FileMeta } from "@rhema/client";
import {
  type ImageFormat,
  jpegInfo,
  pngInfo,
  sniff,
  stripJpeg,
  stripPng,
  stripWebp,
  webpInfo,
} from "./formats";
import { decodeHeic } from "./heic";
import { browserRaster, type Drawable, type Picture, type Raster, type Surface } from "./raster";

/** Long edge of a photo sent at normal quality. */
export const PHOTO_EDGE = 2560;
/** What a photo at normal quality aims to stay under. */
export const PHOTO_BUDGET = 2_500_000;
/** Long edge of the encrypted preview that shows before the photo arrives. */
export const THUMB_EDGE = 320;
/** The preview's size before it becomes a data URL, which adds a third. */
export const THUMB_BUDGET = 25 * 1024;
/** Long edge of the tiny placeholder painted at once and blurred on screen. */
export const PLACEHOLDER_EDGE = 24;
/** How long a photo may take to prepare before it is sent as it is. */
export const PREPARE_MS = 15_000;

const JPEG_LADDER = [0.86, 0.8, 0.74, 0.66, 0.58];
const WEBP_LADDER = [0.84, 0.78, 0.7, 0.62, 0.54];
const THUMB_LADDER = [0.72, 0.62, 0.52, 0.42];
/** The background behind see-through pixels when a preview must be JPEG. */
const LINEN = "#F8F7F3";

export type PreparedPhoto = {
  /** The file to encrypt and send. */
  file: Blob;
  /** "file" when the photo could not be read and goes as a file card instead. */
  kind: "photo" | "file";
  meta: FileMeta;
};

export type PrepareOptions = {
  /** Keep the full size and pixels. Metadata is still removed. */
  original?: boolean;
  raster?: Raster;
  timeoutMs?: number;
};

/** Width and height once a picture of w x h is turned by EXIF orientation o. */
export function orientedSize(w: number, h: number, o: number): [number, number] {
  return o >= 5 && o <= 8 ? [h, w] : [w, h];
}

/** The size that fits within `edge` on the long side, never larger than the picture. */
export function fitWithin(w: number, h: number, edge: number): [number, number] {
  const scale = Math.min(1, edge / Math.max(w, h, 1));
  return [Math.max(1, Math.round(w * scale)), Math.max(1, Math.round(h * scale))];
}

/** The canvas transform that draws a stored picture turned by EXIF orientation o into w x h. */
export function orientationTransform(
  o: number,
  w: number,
  h: number,
): [number, number, number, number, number, number] {
  switch (o) {
    case 2:
      return [-1, 0, 0, 1, w, 0];
    case 3:
      return [-1, 0, 0, -1, w, h];
    case 4:
      return [1, 0, 0, -1, 0, h];
    case 5:
      return [0, 1, 1, 0, 0, 0];
    case 6:
      return [0, 1, -1, 0, w, 0];
    case 7:
      return [0, -1, -1, 0, w, h];
    case 8:
      return [0, -1, 1, 0, 0, h];
    default:
      return [1, 0, 0, 1, 0, 0];
  }
}

/**
 * Draws a picture turned by orientation `o` and scaled to w x h. Large reductions go in halves
 * first, so detail is averaged instead of skipped.
 */
export function render(
  raster: Raster,
  picture: Drawable & { source: unknown },
  o: number,
  w: number,
  h: number,
  background?: string,
): Surface {
  const [tw, th] = o >= 5 && o <= 8 ? [h, w] : [w, h];
  let src: Drawable & { source: unknown } = picture;
  while (src.width / 2 >= tw * 1.0001 && src.height / 2 >= th * 1.0001) {
    const half = raster.surface(
      Math.max(1, Math.ceil(src.width / 2)),
      Math.max(1, Math.ceil(src.height / 2)),
    );
    half.ctx.imageSmoothingEnabled = true;
    half.ctx.imageSmoothingQuality = "high";
    half.ctx.drawImage(src.source as never, 0, 0, half.width, half.height);
    src = half;
  }
  const out = raster.surface(w, h);
  const ctx = out.ctx;
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, w, h);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.setTransform(...orientationTransform(o, w, h));
  ctx.drawImage(src.source as never, 0, 0, tw, th);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return out;
}

/** Encodes at the first quality that fits the budget, or the lowest one. */
export async function encodeWithin(
  surface: Surface,
  type: string,
  budget: number,
  ladder: number[],
): Promise<Blob> {
  let blob: Blob | null = null;
  for (const q of ladder) {
    blob = await surface.encode(type, q);
    if (blob.size <= budget) return blob;
  }
  return blob as Blob;
}

export async function toDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${blob.type};base64,${btoa(s)}`;
}

/**
 * The encrypted preview of about 320 px and the tiny placeholder, from a picture already turned
 * the right way. WebP where the device can encode it, JPEG on a linen background otherwise.
 */
export async function previews(
  raster: Raster,
  picture: Drawable & { source: unknown },
  o = 1,
): Promise<Pick<FileMeta, "thumbnail" | "placeholder">> {
  const webp = await raster.canEncode("image/webp");
  const type = webp ? "image/webp" : "image/jpeg";
  const background = webp ? undefined : LINEN;
  const [ow, oh] = orientedSize(picture.width, picture.height, o);
  let thumb: Blob | null = null;
  for (const edge of [THUMB_EDGE, 240, 160]) {
    const [w, h] = fitWithin(ow, oh, edge);
    thumb = await encodeWithin(
      render(raster, picture, o, w, h, background),
      type,
      THUMB_BUDGET,
      THUMB_LADDER,
    );
    if (thumb.size <= THUMB_BUDGET) break;
  }
  const [pw, ph] = fitWithin(ow, oh, PLACEHOLDER_EDGE);
  const tiny = await render(raster, picture, o, pw, ph, LINEN).encode("image/jpeg", 0.5);
  return {
    ...(thumb && thumb.size <= THUMB_BUDGET ? { thumbnail: await toDataUrl(thumb) } : {}),
    placeholder: await toDataUrl(tiny),
  };
}

function renamed(name: string | undefined, ext: string): string | undefined {
  if (!name) return undefined;
  const dot = name.lastIndexOf(".");
  return `${dot > 0 ? name.slice(0, dot) : name}.${ext}`;
}

const EXT: Record<string, string> = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };

function withTimeout<T>(job: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    job,
    new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(new Error("timeout")), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

type Decoded = {
  picture: Picture;
  orientation: number;
  alpha: boolean;
  animated: boolean;
  format: ImageFormat;
};

async function decode(file: Blob, format: ImageFormat, raster: Raster): Promise<Decoded> {
  switch (format) {
    case "jpeg": {
      const info = await jpegInfo(file);
      // The EXIF block goes before decoding, so every browser gives the pixels as stored.
      const picture = await raster.decode(await stripJpeg(file, false));
      return { picture, orientation: info.orientation, alpha: false, animated: false, format };
    }
    case "png": {
      const info = await pngInfo(file);
      return {
        picture: await raster.decode(file),
        orientation: 1,
        alpha: info.alpha,
        animated: info.animated,
        format,
      };
    }
    case "webp": {
      const info = await webpInfo(file);
      return {
        picture: await raster.decode(file),
        orientation: 1,
        alpha: info.alpha,
        animated: info.animated,
        format,
      };
    }
    case "heic":
      return {
        picture: await decodeHeic(file, raster),
        orientation: 1,
        alpha: false,
        animated: false,
        format,
      };
    case "gif":
      return { picture: await raster.decode(file), orientation: 1, alpha: true, animated: true, format };
    default:
      return { picture: await raster.decode(file), orientation: 1, alpha: false, animated: false, format };
  }
}

/** The file with its metadata removed and its pixels untouched, where Rhema knows how. */
async function stripped(file: Blob, format: ImageFormat): Promise<Blob> {
  if (format === "jpeg") return stripJpeg(file, true);
  if (format === "png") return stripPng(file);
  if (format === "webp") return stripWebp(file);
  return file;
}

async function prepare(file: Blob, opts: PrepareOptions, raster: Raster): Promise<PreparedPhoto> {
  const name = (file as File).name || undefined;
  const format = await sniff(file);
  const d = await decode(file, format, raster);
  try {
    const [ow, oh] = orientedSize(d.picture.width, d.picture.height, d.orientation);
    const fits = Math.max(ow, oh) <= PHOTO_EDGE && file.size <= PHOTO_BUDGET;
    const keepAsIs = format !== "heic" && (opts.original || fits || d.animated);
    const meta: FileMeta = { width: ow, height: oh, ...(await previews(raster, d.picture, d.orientation)) };

    if (keepAsIs) {
      const out = await stripped(file, format);
      const mime = format === "other" ? file.type || "application/octet-stream" : out.type || file.type;
      return { file: out, kind: "photo", meta: { ...meta, mime, ...(name ? { name } : {}) } };
    }

    const [w, h] = opts.original ? [ow, oh] : fitWithin(ow, oh, PHOTO_EDGE);
    let blob: Blob;
    if (d.alpha) {
      // See-through pixels need PNG or WebP. PNG keeps them exactly when it fits the budget.
      const surface = render(raster, d.picture, d.orientation, w, h);
      blob = await surface.encode("image/png");
      if (blob.size > PHOTO_BUDGET && (await raster.canEncode("image/webp"))) {
        blob = await encodeWithin(surface, "image/webp", PHOTO_BUDGET, WEBP_LADDER);
      }
    } else {
      const surface = render(raster, d.picture, d.orientation, w, h);
      blob = opts.original
        ? await surface.encode("image/jpeg", 0.92)
        : await encodeWithin(surface, "image/jpeg", PHOTO_BUDGET, JPEG_LADDER);
    }
    const ext = EXT[blob.type] ?? "jpg";
    const newName = renamed(name, ext);
    return {
      file: newName ? new File([blob], newName, { type: blob.type }) : blob,
      kind: "photo",
      meta: { ...meta, width: w, height: h, mime: blob.type, ...(newName ? { name: newName } : {}) },
    };
  } finally {
    d.picture.close();
  }
}

/**
 * A photo ready to encrypt, with its size, preview and placeholder. When it cannot be read in
 * time it is sent as it is: a HEIC photo as a file, because most devices could not show it, and
 * anything else as a photo without a preview, as before.
 */
export async function preparePhoto(file: Blob, opts: PrepareOptions = {}): Promise<PreparedPhoto> {
  const raster = opts.raster ?? browserRaster;
  try {
    return await withTimeout(prepare(file, opts, raster), opts.timeoutMs ?? PREPARE_MS);
  } catch {
    const name = (file as File).name || undefined;
    const meta: FileMeta = { mime: file.type || "application/octet-stream", ...(name ? { name } : {}) };
    const heic = await sniff(file)
      .then((f) => f === "heic")
      .catch(() => false);
    if (heic) return { file, kind: "file", meta };
    // Even unread, a JPEG or PNG loses its metadata, which only needs the headers.
    const format = await sniff(file).catch(() => "other" as ImageFormat);
    const out = await stripped(file, format).catch(() => file);
    return { file: out, kind: "photo", meta };
  }
}
